import { ModalProps } from '@components/V4/Modal/Modal';
import { useState } from 'react';
import MultiPageModal, {
  NavigationProps,
} from '../../../../components/V4/Modal/MultiPageModal/MultiPageModal';
import EnrollmentDetailsPage from './EnrollmentDetailsPage/EnrollmentDetailsPage';
import MentorDetailsPage from './MentorDetailsPage/MentorDetailsPage';
import { MentorTab } from './MentorDetailsPage/MentorTabsSection/MentorTabsSection';
import SessionDetailsPage from './SessionDetailsPage/SessionDetailsPage';
import { SessionTab } from './SessionDetailsPage/SessionTabsSection/SessionTabsSection';
import StudentDetailsPage from './StudentDetailsPage/StudentDetailsPage';
import { StudentTab } from './StudentDetailsPage/StudentTabsSection/StudentTabsSection';
import EnrollmentsDetailsPage from './EnrollmentsDetailsPage/EnrollmentsDetailsPage';
import SurveyDetailsPage from './SurveyDetailsPage/SurveyDetailsPage';

export type DetailsModalPage =
  | 'mentor'
  | 'student'
  | 'enrollment'
  | 'session'
  | 'enrollments'
  | 'survey'
  | undefined;

type ModalBaseProps = ModalProps & {
  mentorId?: string;
  studentId?: string;
  enrollmentId?: string;
  enrollmentsIds?: string[];
  sessionId?: string;
};

export type DetailsModalProps =
  | (ModalBaseProps & { page?: 'student'; tab?: StudentTab })
  | (ModalBaseProps & { page?: 'session'; tab?: SessionTab })
  | (ModalBaseProps & { page?: 'mentor'; tab?: MentorTab })
  | (ModalBaseProps & { page?: 'enrollment'; tab?: undefined })
  | (ModalBaseProps & { page?: 'enrollments'; tab?: undefined })
  | (ModalBaseProps & { page?: 'survey'; tab?: undefined });

const DetailsModal = (props: DetailsModalProps) => {
  const {
    mentorId: receivedMentorId,
    studentId: receivedStudentId,
    page,
    enrollmentId: receivedEnrollmentId,
    enrollmentsIds: receivedEnrollmentsIds,
    sessionId: receivedSessionId,
    isOpen,
    onClose,
    tab,
  } = props;

  const [enrollmentId, setEnrollmentId] = useState<string | undefined>(receivedEnrollmentId);
  const [enrollmentsIds, setEnrollmentsIds] = useState<string[] | undefined>(
    receivedEnrollmentsIds
  );
  const [sessionId, setSessionId] = useState<string | undefined>(receivedSessionId);
  const [studentId, setStudentId] = useState<string | undefined>(receivedStudentId);
  const [mentorId, setMentorId] = useState<string | undefined>(receivedMentorId);

  const enrollmentModalOpenHandler = (id: string, goToPage: (page: string) => void) => {
    setEnrollmentId(id);
    goToPage('enrollment');
  };

  const sessionModalOpenHandler = (sessionId: string, goToPage: (page: string) => void) => {
    setSessionId(sessionId);
    goToPage('session');
  };

  const studentModalOpenHandler = (studentId: string, goToPage: (page: string) => void) => {
    setStudentId(studentId);
    goToPage('student');
  };

  const mentorModalOpenHandler = (mentorId: string, goToPage: (page: string) => void) => {
    setMentorId(mentorId);
    goToPage('mentor');
  };

  const pages = {
    mentor: ({ goToPage, goBack, isBackButtonActive }: NavigationProps) => (
      <MentorDetailsPage
        mentorId={mentorId ?? ''}
        tab={tab as MentorTab}
        isBackButtonActive={isBackButtonActive}
        onGoBack={goBack}
        onEnrollmentModalOpen={(id: string) => enrollmentModalOpenHandler(id, goToPage)}
        onSessionModalOpen={(id: string) => sessionModalOpenHandler(id, goToPage)}
        onStudentModalOpen={(studentId: string) => studentModalOpenHandler(studentId, goToPage)}
      />
    ),
    student: ({ goToPage, goBack, isBackButtonActive }: NavigationProps) => (
      <StudentDetailsPage
        studentId={studentId ?? ''}
        tab={tab as StudentTab}
        isBackButtonActive={isBackButtonActive}
        onGoBack={goBack}
        onEnrollmentModalOpen={(id: string) => enrollmentModalOpenHandler(id, goToPage)}
        onSessionModalOpen={(id: string) => sessionModalOpenHandler(id, goToPage)}
        onMentorModalOpen={(id: string) => mentorModalOpenHandler?.(id, goToPage)}
      />
    ),
    enrollment: ({ goToPage, goBack, isBackButtonActive }: NavigationProps) => (
      <EnrollmentDetailsPage
        isBackButtonActive={isBackButtonActive}
        enrollmentId={enrollmentId ?? ''}
        onGoBack={goBack}
        onSessionModalOpen={(sessionId: string) => sessionModalOpenHandler(sessionId, goToPage)}
      />
    ),
    enrollments: ({ goToPage, goBack, isBackButtonActive }: NavigationProps) => (
      <EnrollmentsDetailsPage
        isBackButtonActive={isBackButtonActive}
        enrollmentsIds={enrollmentsIds ?? []}
        onGoBack={goBack}
        onEnrollmentModalOpen={(id: string) => enrollmentModalOpenHandler(id, goToPage)}
        onMentorModalOpen={(id: string) => mentorModalOpenHandler?.(id, goToPage)}
        onStudentModalOpen={(studentId: string) => studentModalOpenHandler(studentId, goToPage)}
      />
    ),
    session: ({ goBack, isBackButtonActive }: NavigationProps) => (
      <SessionDetailsPage
        isBackButtonActive={isBackButtonActive}
        sessionId={sessionId ?? ''}
        onGoBack={goBack}
        tab={tab as SessionTab}
      />
    ),
    survey: ({ goBack, isBackButtonActive }: NavigationProps) => (
      <SurveyDetailsPage
        isBackButtonActive={isBackButtonActive}
        enrollmentId={enrollmentId ?? ''}
        onGoBack={goBack}
      />
    ),
  };

  return (
    <MultiPageModal
      size="large"
      className="min-h-[616px]"
      isOpen={isOpen}
      onClose={onClose}
      initialPage={page || 'student'}
      pages={pages}
    />
  );
};

export default DetailsModal;
